export const SANDBOX_ACCESS = Object.freeze({
  FULL: "full",
  VIEW: "view",
  DISABLED: "disabled",
  HIDDEN: "hidden",
});

/** Flip to true when sandbox access restrictions are ready to ship. */
export const SANDBOX_FEATURE_GATING_ENABLED = false;

/** Flip to true after release to show Sandbox in settings and the profile menu. */
export const SANDBOX_ENTRY_POINTS_ENABLED = false;

export const SANDBOX_FEATURE = Object.freeze({
  PAYMENTS: "payments",
  PAYMENT_MUTATIONS: "payment-mutations",
  SUBSCRIPTIONS: "subscriptions",
  BUSINESS_SETTINGS: "business-settings",
  REPORTS: "reports",
  MESSAGES: "messages",
  DOCUMENTS: "documents",
  REPORT_CALENDAR: "report-calendar",
  TAX_AUTHORITY: "tax-authority",
  ADD_COMPANY: "add-company",
  DELETE_COMPANY: "delete-company",
  EMAIL_NOTIFICATIONS: "email-notifications",
});

const SANDBOX_FEATURE_ACCESS = Object.freeze({
  [SANDBOX_FEATURE.PAYMENTS]: SANDBOX_ACCESS.VIEW,
  [SANDBOX_FEATURE.PAYMENT_MUTATIONS]: SANDBOX_ACCESS.HIDDEN,
  [SANDBOX_FEATURE.SUBSCRIPTIONS]: SANDBOX_ACCESS.VIEW,
  [SANDBOX_FEATURE.BUSINESS_SETTINGS]: SANDBOX_ACCESS.VIEW,
  [SANDBOX_FEATURE.REPORTS]: SANDBOX_ACCESS.DISABLED,
  [SANDBOX_FEATURE.MESSAGES]: SANDBOX_ACCESS.DISABLED,
  [SANDBOX_FEATURE.DOCUMENTS]: SANDBOX_ACCESS.DISABLED,
  [SANDBOX_FEATURE.REPORT_CALENDAR]: SANDBOX_ACCESS.DISABLED,
  [SANDBOX_FEATURE.TAX_AUTHORITY]: SANDBOX_ACCESS.HIDDEN,
  [SANDBOX_FEATURE.ADD_COMPANY]: SANDBOX_ACCESS.HIDDEN,
  [SANDBOX_FEATURE.DELETE_COMPANY]: SANDBOX_ACCESS.HIDDEN,
  [SANDBOX_FEATURE.EMAIL_NOTIFICATIONS]: SANDBOX_ACCESS.HIDDEN,
});

export function isSandboxCompany(user) {
  return user?.company?.is_sandbox === true;
}

export function shouldApplySandboxRestrictions(user) {
  return SANDBOX_FEATURE_GATING_ENABLED && isSandboxCompany(user);
}

export function getSandboxFeatureAccessPolicy(feature) {
  return SANDBOX_FEATURE_ACCESS[feature] || SANDBOX_ACCESS.FULL;
}

export function getSandboxFeatureAccess(feature, isSandbox) {
  if (!SANDBOX_FEATURE_GATING_ENABLED || !isSandbox) return SANDBOX_ACCESS.FULL;
  return getSandboxFeatureAccessPolicy(feature);
}

export function isSandboxFeatureBlocked(feature, isSandbox) {
  const access = getSandboxFeatureAccess(feature, isSandbox);
  return access === SANDBOX_ACCESS.DISABLED || access === SANDBOX_ACCESS.HIDDEN;
}

export function canMutateSandboxFeature(feature, isSandbox) {
  return getSandboxFeatureAccess(feature, isSandbox) === SANDBOX_ACCESS.FULL;
}

export function isSandboxState(state) {
  return isSandboxCompany(state?.user?.user);
}

/**
 * `condition` for createAsyncThunk: skips the request when the feature is not
 * available in a sandbox company, so the API is never called and no 403 arrives.
 */
export function makeSandboxBlockedCondition(feature) {
  return (_arg, { getState }) =>
    !isSandboxFeatureBlocked(feature, isSandboxState(getState()));
}

/**
 * `condition` for createAsyncThunk protecting mutations: allows the request only
 * when the feature has full access (read-only sandbox features are blocked too).
 */
export function makeSandboxMutationCondition(feature) {
  return (_arg, { getState }) =>
    canMutateSandboxFeature(feature, isSandboxState(getState()));
}
